import saveAs from 'file-saver';
import { IPositionSummary } from '../../../../Models/IPositionSummary';
import { ApiService } from '../../../../Services/ApiService';
import { NavItem } from './BlindTestDashboardTypes';
import logger from '../../../../Services/LoggingService';

/**
 * Factory function to create API operations with proper closure scope
 */
export const createBlindTestDashboardApiOperations = (
  apiService: ApiService,
  logPerformance: (label: string, startTime: number) => void
) => {
  /**
   * Get position design session summary
   */
  const getPositionDesignSessionSummary = async (
    industry: string,
    subindustry: string,
    application: string,
    subapplication: string,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
    setShowError: React.Dispatch<React.SetStateAction<boolean>>,
    setActiveCardId: React.Dispatch<React.SetStateAction<string | null>>,
    setPositionSummaries: React.Dispatch<React.SetStateAction<IPositionSummary[]>>
  ) => {
    const startTime = performance.now();
    setIsLoading(true);
    setError(null);
    setActiveCardId(null); // Reset active card when loading new data

    try {
      logger.debug('Fetching position design session summary', {
        industry,
        subindustry,
        application,
        subapplication
      }, {
        component: 'BlindTestDashboard',
        method: 'getPositionDesignSessionSummary'
      });

      const fetchStart = performance.now();
      const response = await apiService.getAsync(
        `blindtests/designsessions/summary`,
        {
          industry,
          subIndustry: subindustry,
          application,
          subApplication: subapplication,
          processStage: "",
          productLine: "",
          alfaLavalPosition: "",
          breakdownOfProcessStage: "",
          coldMediaType: "",
          hotMediaType: "",
          inputs: [],
          outputs: [],
          nonUsedProperties: [],
        }
      );
      logPerformance('API fetch position summaries', fetchStart);

      if (!response.ok) {
        throw new Error(
          `Error getting summaries: ${response.status} ${response.statusText}`
        );
      }

      const parseStart = performance.now();
      const result: IPositionSummary[] = await response.json();
      logPerformance('Parse position summaries JSON', parseStart);

      logger.info('Position summaries received from API', { 
        count: result.length 
      }, {
        component: 'BlindTestDashboard',
        method: 'getPositionDesignSessionSummary'
      });

      setPositionSummaries(result);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      logger.error("Failed to get position design session summary", err as Error, {
        industry: industry,
        subindustry: subindustry,
        application: application,
        subapplication: subapplication
      }, {
        component: 'BlindTestDashboard',
        method: 'getPositionDesignSessionSummary'
      });
      setError(errorMessage);
      setShowError(true);
    } finally {
      setIsLoading(false);
      logPerformance('getPositionDesignSessionSummary total', startTime);
    }
  };

  /**
   * Load SME navigation links
   */
  const loadNavigationLinks = async (
    setError: React.Dispatch<React.SetStateAction<string | null>>,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setShowError: React.Dispatch<React.SetStateAction<boolean>>,
    setNavItems: React.Dispatch<React.SetStateAction<NavItem[]>>,
    displaySummaries: boolean
  ) => {
    const startTime = performance.now();
    setError(null);
    setIsLoading(true); // Ensure loading state is set at the beginning
    
    try {
      logger.debug('Fetching navigation links', null, {
        component: 'BlindTestDashboard',
        method: 'loadNavigationLinks'
      });

      const fetchStart = performance.now();
      const response = await apiService.getAsync(
        `settings/productnavigation`
      );
      logPerformance('API fetch navigation links', fetchStart);

      if (!response.ok) {
        throw new Error(
          `There was an issue getting SME navigation [${response.status}]: ${response.statusText}`
        );
      }

      const parseStart = performance.now();
      const result = await response.json();
      logPerformance('Parse navigation JSON', parseStart);

      logger.debug("Navigation items loaded from API", {
        itemCount: result?.length || 0
      }, {
        component: 'BlindTestDashboard',
        method: 'loadNavigationLinks'
      });
      
      // Deduplicate navigation items before setting state
      const deduplicateStart = performance.now();
      const deduplicatedNavItems = deduplicateNavigationItems(result);
      logPerformance('Deduplicate navigation items', deduplicateStart);
      
      logger.debug("Navigation items deduplicated", {
        originalCount: result?.length || 0,
        deduplicatedCount: deduplicatedNavItems?.length || 0
      }, {
        component: 'BlindTestDashboard',
        method: 'loadNavigationLinks'
      });
      
      setNavItems(deduplicatedNavItems);
      
      // Only set isLoading to false if we don't need to load position summaries
      if (!displaySummaries) {
        setIsLoading(false);
      }
    } catch (exception: any) {
      const errorMessage = `Could not contact the server. Error: ${exception}`;
      logger.error("Failed to load navigation links", exception as Error, null, {
        component: 'BlindTestDashboard',
        method: 'loadNavigationLinks'
      });
      setError(errorMessage);
      setShowError(true);
      setIsLoading(false); // Set loading to false on error
    } finally {
      logPerformance('loadNavigationLinks total', startTime);
    }
  };

  /**
   * Download CSV file
   */
  const downloadCSVFile = async (
    datasetId: string,
    item: IPositionSummary,
    setCardDownloading: (cardId: string, isDownloading: boolean) => void,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
    setShowError: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    const startTime = performance.now();
    // Generate card identifier
    const cardId = `${item.alfaLavalPosition}-${item.productLine}-${item.blindTestDataSetId}`;

    // Set downloading state for this specific card
    setCardDownloading(cardId, true);
    setError(null);

    try {
      logger.debug('Starting CSV download', {
        datasetId,
        position: item.alfaLavalPosition,
        productLine: item.productLine
      }, {
        component: 'BlindTestDashboard',
        method: 'downloadCSVFile'
      });

      const fetchStart = performance.now();
      const response = await apiService.getAsync(
        `designsessions/${datasetId}/download`
      );
      logPerformance('API fetch CSV download', fetchStart);

      if (!response.ok) {
        throw new Error(
          `Could not download CSV: ${response.status} ${response.statusText}`
        );
      }

      // Create filename with position and dataset ID
      const filename = `${item.alfaLavalPosition}-${item.blindTestDataSetId}.csv`;
      
      const convertStart = performance.now();
      await convertStreamToCsv(response.body!, filename);
      logPerformance('Convert stream to CSV', convertStart);

      logger.info('CSV download completed successfully', {
        filename,
        datasetId
      }, {
        component: 'BlindTestDashboard',
        method: 'downloadCSVFile'
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to download file";
      logger.error("Failed to download CSV file", err as Error, {
        datasetId: datasetId,
        position: item.alfaLavalPosition,
        productLine: item.productLine
      }, {
        component: 'BlindTestDashboard',
        method: 'downloadCSVFile'
      });
      setError(errorMessage);
      setShowError(true);
    } finally {
      // Reset downloading state for this specific card
      setCardDownloading(cardId, false);
      logPerformance('downloadCSVFile total', startTime);
    }
  };

  return {
    getPositionDesignSessionSummary,
    loadNavigationLinks,
    downloadCSVFile
  };
};

/**
 * Convert stream to CSV and save
 */
const convertStreamToCsv = async (stream: ReadableStream, filename: string) => {
  const reader = stream.getReader();
  let csvData = "";
  while (true) {
    const result = await reader.read();
    if (result.done) {
      break;
    }
    csvData += new TextDecoder().decode(result.value);
  }
  const blob = new Blob([csvData], { type: "text/csv" });
  saveAs(blob, filename);
};

/**
 * Remove duplicate navigation items at each level of the hierarchy
 */
const deduplicateNavigationItems = (items: NavItem[]): NavItem[] => {
  // First deduplicate at the current level using a Map to preserve only first occurrence
  const uniqueItemsMap = new Map<string, NavItem>();
  
  items.forEach(item => {
    if (!uniqueItemsMap.has(item.name)) {
      uniqueItemsMap.set(item.name, item);
    }
  });
  
  // Convert map back to array
  const uniqueItems = Array.from(uniqueItemsMap.values());
  
  // Recursively deduplicate children
  return uniqueItems.map(item => {
    if (item.children && item.children.length > 0) {
      return {
        ...item,
        children: deduplicateNavigationItems(item.children)
      };
    }
    return item;
  });
};

/**
 * Data processing operations factory
 */
export const createBlindTestDashboardDataOperations = (
  logPerformance: (label: string, startTime: number) => void
) => {
  /**
   * Process position summaries for display
   */
  const processPositionSummaries = (
    summaries: IPositionSummary[],
    activeCardId: string | null
  ) => {
    const startTime = performance.now();
    
    // Sort summaries by priority (todo count desc, then alphabetically)
    const processed = summaries.sort((a, b) => {
      const aTodoCount = a.summary["todo"] || 0;
      const bTodoCount = b.summary["todo"] || 0;
      
      // First priority: todo count (descending)
      if (aTodoCount !== bTodoCount) {
        return bTodoCount - aTodoCount;
      }
      
      // Second priority: position name (ascending)
      return a.alfaLavalPosition.localeCompare(b.alfaLavalPosition);
    });
    
    logPerformance('Process position summaries', startTime);
    return processed;
  };

  /**
   * Calculate summary statistics
   */
  const calculateSummaryStats = (summaries: IPositionSummary[]) => {
    const startTime = performance.now();
    
    const stats = summaries.reduce(
      (acc, item) => {
        acc.totalCards++;
        acc.totalTodo += item.summary["todo"] || 0;
        acc.totalCompleted += 
          (item.summary["emelie - bts"] || 0) +
          (item.summary["emelie - acceptable"] || 0) +
          (item.summary["sme"] || 0);
        
        if (item.summary["todo"] === 0) {
          acc.completedCards++;
        }
        
        return acc;
      },
      {
        totalCards: 0,
        completedCards: 0,
        totalTodo: 0,
        totalCompleted: 0
      }
    );
    
    logPerformance('Calculate summary stats', startTime);
    return stats;
  };

  return {
    processPositionSummaries,
    calculateSummaryStats
  };
}; 